import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Typography, Box, Paper, markdownTableComponents } from 'src/components';
import markdownCustomComponents from './markdown-components'; 
import { Link as RouterLink } from 'react-router-dom';

export default function Markdown({ children }) {
  const markdownComponents = {
    h1: ({ children }) => {
      const id = children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');
      return <Typography variant="h2" component="h1" id={id} sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>{children}</Typography>;
    },
    h2: ({ children }) => {
      const id = children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');
      return <Typography variant="h3" component="h2" id={id} sx={{ mt: 3, mb: 2, fontWeight: 'bold' }}>{children}</Typography>;
    },
    h3: ({ children }) => {
      const id = children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');
      return <Typography variant="h4" component="h3" id={id} sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>{children}</Typography>;
    },
    p: ({ node, children }) => {
      const hasBlockChildren = React.Children.toArray(children).some(
        (child) => React.isValidElement(child)
      );

      return (
        <Typography
          variant="body1"
          paragraph={!hasBlockChildren}
          component={hasBlockChildren ? "div" : "p"}
          sx={{ lineHeight: 1.7 }}
        >
          {children}
        </Typography>
      );
    },
    ul: ({ children }) => <Box component="ul" sx={{ pl: 3, mb: 2 }}>{children}</Box>,
    ol: ({ children }) => <Box component="ol" sx={{ pl: 3, mb: 2 }}>{children}</Box>,
    li: ({ children }) => <Typography component="li" variant="body1" sx={{ mb: 0.5, lineHeight: 1.6 }}>{children}</Typography>,
    blockquote: ({ children }) => (
        <Paper
            component="blockquote"
            elevation={0}
            sx={{
            borderLeft: 4,
            borderColor: 'primary.main',
            pl: 2,
            pt: 1,
            pb: 0,
            my: 2,
            bgcolor: 'grey.50',
            m: 0
        }}>
            <Typography variant="body2">
              {children}
            </Typography>
        </Paper>
    ),
    img: ({ src, alt }) => {
      // Parse optional caption and link from alt text
      let caption = null;
      let link = null;

      // Only parse caption/link if the alt text includes a pipe
      if (alt?.includes('|')) {
        const [rawCaption, rawLink] = alt.split('|').map(str => str.trim());
        caption = rawCaption || null;
        link = rawLink || null;
      }

      return (
        <Box component="figure" sx={{ my: 3 }}>
          <Box
            component="img"
            src={src}
            alt={caption || ''}
            sx={{
              width: '100%',
              maxHeight: 400,
              objectFit: 'contain',
              borderRadius: 1,
            }}
          />
          {caption && (
            <Typography
              variant="caption"
              component="figcaption"
              sx={{
                mt: 1,
                color: 'text.secondary',
                textAlign: 'center',
              }}
            >
              {link ? (
                <a href={link} target="_blank" rel="noopener noreferrer" 
                  style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}>
                  {caption}
                </a>
              ) : (
                caption
              )}
            </Typography>
          )}
        </Box>
      );
    },
    // a: ({ href, children }) => (
    //   <Typography
    //     component="a"
    //     href={href}
    //     target="_blank"
    //     rel="noopener noreferrer"
    //     color="primary"
    //     fontWeight={600}
    //     sx={{ textDecoration: 'underline', fontSize: 'inherit', lineHeight: 'inherit' }}
    //   >
    //     {children}
    //   </Typography>
    // ),
    a: ({ href = '', children }) => {
      const isInternal =
        href.startsWith('/') ||
        href.includes('panamhighway.com');

      if (isInternal) {
        // Normalize full URLs to paths
        const to = href.replace(/^https?:\/\/(www\.)?panamhighway\.com/, '');

        return (
          <Typography
            component={RouterLink}
            to={to}
            color="primary"
            fontWeight={600}
            sx={{
              textDecoration: 'underline',
              fontSize: 'inherit',
              lineHeight: 'inherit',
            }}
          >
            {children}
          </Typography>
        );
      }

      // External link
      return (
        <Typography
          component="a"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          color="primary"
          fontWeight={600}
          sx={{
            textDecoration: 'underline',
            fontSize: 'inherit',
            lineHeight: 'inherit',
          }}
        >
          {children}
        </Typography>
      );
    },
    ...markdownCustomComponents, // inject custom HTML elements like <affiliate-card>
    ...markdownTableComponents,
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={markdownComponents}
    >
      {children}
    </ReactMarkdown>
  );
}
